// Vite entry. React 19 concurrent root + i18n boot before App mounts so
// the first paint already speaks the right language (no FOUC of English
// text flashing into Turkish).

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

// IMPORT ORDER IS LOAD-BEARING: @/boot-failure installs the window error
// listeners at module scope, and ES modules evaluate depth-first in import
// order. It must come before @/App (whose transitive imports run fail-closed
// deployment-config guards that THROW during evaluation) or a boot failure
// stays invisible — see the header comment in src/boot-failure.ts.
import { markBootComplete, reportBootFailure } from "@/boot-failure";
import "@/styles/globals.css";
import { App } from "@/App";
import { bootstrapDevAuth } from "@/auth/dev-auth";
import { initI18n } from "@/i18n";

async function bootstrap(): Promise<void> {
  // DEV-ONLY (no-op in production builds — see src/auth/dev-auth.ts): pre-seed
  // a local 8h session into the OIDC store BEFORE <App/> mounts AuthProvider,
  // so react-oidc-context hydrates as signed-in and RequireAuth never kicks
  // off a Keycloak redirect. Awaited so the first paint already has a user.
  await bootstrapDevAuth();

  const i18n = await initI18n();

  // Sync <html lang> with the resolved locale so screen readers pronounce
  // content correctly (WCAG 2.2 Level A — 3.1.1 Language of Page +
  // CLAUDE.md WCAG invariant). Re-sync on every locale change.
  document.documentElement.lang = i18n.language;
  i18n.on("languageChanged", (lng) => {
    document.documentElement.lang = lng;
  });

  const container = document.getElementById("root");
  if (!container) {
    throw new Error("customer-web: #root element missing from index.html");
  }

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  // The app owns the screen from here; later failures are the route error
  // boundaries' business, not the boot surface's.
  markBootComplete();
}

bootstrap().catch(reportBootFailure);
